import { useMemo } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useApps } from '../components/AppPicker';
import { PageHeader } from '../components/Layout';
import { Button, Input, Select, Spinner, cn } from '../components/ui';
import type { AppKey, InsightPeriod } from '../lib/types';
import { AllTimeView } from './countries/AllTime';
import { PeriodView } from './countries/PeriodView';

type View = 'all' | InsightPeriod;

const VIEWS: { value: View; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

/**
 * Devices by country: an all-time breakdown, or one day, week or month
 * against the period before it.
 *
 * View, date and app live in the URL, so a reload or a shared link lands on
 * the same period. Each view fetches only while it's on screen and nothing
 * is polled — the backend caches every breakdown, a finished period longest.
 */
export function CountriesPage() {
  const [params, setParams] = useSearchParams();
  const apps = useApps();
  const today = localToday();

  const view = (VIEWS.some((v) => v.value === params.get('view'))
    ? params.get('view')
    : 'all') as View;
  const rawDate = params.get('date');
  const date =
    rawDate && /^\d{4}-\d{2}-\d{2}$/.test(rawDate) && rawDate <= today
      ? rawDate
      : today;
  const appKey = (params.get('app') || undefined) as AppKey | undefined;

  const update = (next: { view?: View; date?: string; app?: string }) => {
    const merged = { view, date, app: appKey ?? '', ...next };
    const out = new URLSearchParams();
    if (merged.view !== 'all') out.set('view', merged.view);
    if (merged.view !== 'all' && merged.date !== today) out.set('date', merged.date);
    if (merged.app) out.set('app', merged.app);
    setParams(out, { replace: true });
  };

  const fetching = useIsFetching({
    predicate: (query) => String(query.queryKey[0]).startsWith('countries'),
  });

  const previous = useMemo(
    () => (view === 'all' ? date : shiftDate(date, view, -1)),
    [date, view],
  );
  const next = useMemo(
    () => (view === 'all' ? date : shiftDate(date, view, 1)),
    [date, view],
  );
  // A period that hasn't started yet has nothing in it.
  const canGoNext = view !== 'all' && periodStart(next, view) <= today;

  return (
    <>
      <PageHeader
        title="Countries"
        description="Where devices come from, and how each market converts, grows and engages"
        action={
          <div className="flex items-center gap-2">
            {fetching > 0 && <Spinner className="size-4" />}
            <Select
              value={appKey ?? ''}
              onChange={(event) => update({ app: event.target.value })}
              className="w-auto py-1.5 text-sm"
              aria-label="App"
            >
              <option value="">All apps</option>
              {apps.data?.map((app) => (
                <option key={app.app_key} value={app.app_key}>
                  {app.app_label}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div
          role="tablist"
          aria-label="Period"
          className="inline-flex rounded-lg border border-line bg-surface p-0.5"
        >
          {VIEWS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={view === option.value}
              onClick={() => update({ view: option.value })}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                view === option.value
                  ? 'bg-brand-soft text-brand'
                  : 'text-ink-2 hover:text-ink',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {view !== 'all' && (
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              onClick={() => update({ date: previous })}
              aria-label={`Previous ${view}`}
              icon={<ChevronLeft size={16} />}
            />
            <Input
              type="date"
              value={date}
              max={today}
              onChange={(event) =>
                event.target.value && update({ date: event.target.value })
              }
              className="w-auto py-1.5 text-sm"
              aria-label="Date"
            />
            <Button
              variant="ghost"
              onClick={() => update({ date: next > today ? today : next })}
              disabled={!canGoNext}
              aria-label={`Next ${view}`}
              icon={<ChevronRight size={16} />}
            />
            {date !== today && (
              <Button
                variant="ghost"
                className="text-xs"
                onClick={() => update({ date: today })}
              >
                Today
              </Button>
            )}
          </div>
        )}
      </div>

      {view === 'all' ? (
        <AllTimeView appKey={appKey} />
      ) : (
        <PeriodView
          appKey={appKey}
          period={view}
          date={date}
          onOpenDay={(day) => update({ view: 'day', date: day })}
        />
      )}
    </>
  );
}

// ── dates ───────────────────────────────────────────────────────────────────
// Plain `YYYY-MM-DD` strings in the operator's timezone — the same form the
// backend resolves periods from — so they compare correctly as strings.

function localToday(): string {
  return toIso(new Date());
}

function toIso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parse(date: string): Date {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** [date] moved one [period] earlier or later. */
function shiftDate(date: string, period: InsightPeriod, step: 1 | -1): string {
  const d = parse(date);
  if (period === 'day') d.setDate(d.getDate() + step);
  else if (period === 'week') d.setDate(d.getDate() + 7 * step);
  else {
    // Clamp the day, so Mar 31 → Feb 28 rather than rolling into March.
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + step);
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, lastDay));
  }
  return toIso(d);
}

/** First day of the period containing [date]; weeks start on Monday. */
function periodStart(date: string, period: InsightPeriod): string {
  const d = parse(date);
  if (period === 'week') d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  else if (period === 'month') d.setDate(1);
  return toIso(d);
}
